import React, { useState, useCallback, useEffect } from 'react';
import { IView } from 'Framework';
import { strings } from 'src/localization';
import { SpecData, ApptCust, DepartmentSpec, ApptOps, UpdateApptOpsInp, DeteremineDeptInp, OpType, invalidDates, VehicleRecs, ascCtaInteractionInput, ascPageviewInput, ascRetailProcessInput, ascElementConfigurationInput, ApptVeh } from 'src/models';
import css from './AddSkipServices.module.scss';
import SkipLightBox from './SkipLightBox/SkipLightBox';
import AddSkipButton from './AddSkipButtons/AddSkipButton';
import { ComponentMapIds } from 'src/components/map';
import TextInCircle from 'src/components/TextInCircle/TextInCircle';
import { IServicesState } from 'src/store/states/AdditionalServices/AdditionalServicesSlice';
import { BlankDeptSpec } from 'src/models/Specs/TestSpecs';
import { LoadingProgress, ScrollBar } from '@framework-controls/core';
import Footer from '../Footer/Footer';
import SVPButton from '../SVPButtons/SVPButton';
import { ascCtaInteraction, ascPageview, ascRetailProcess, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';
import { getProviderId, getSdMileage, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface IProps {
    specData?: SpecData;
    apptVin?: string;
    apptCust?: ApptCust;
    inquiryInvoked: boolean;
    showWeOwes?: boolean;
    showOEMRecall?: boolean;
    showPrvRecom?: boolean;
    specProfile: string;
    additionalServices: IServicesState;
    specDeptData: DepartmentSpec;
    specDefaultDept: string;
    apptOps: ApptOps[];
    invalidDates: invalidDates[];
    invalidDatesDept: string;
    apptDept: string;
    selectedVehicle?: string;
    vehicles: VehicleRecs;
    apptVeh: ApptVeh;
    pushToMainPanes: (inputView: IView) => void;
    getRMTimes: (specProfile) => void;
    getInvalidDates: (pecProfile: string) => void;
    getDeptSpecs: () => void;
    UpdateApptOps: (inputOps: UpdateApptOpsInp[]) => void;
    determineDept: (inp: DeteremineDeptInp) => void;
    clearInvalidDates: () => void;
    clearAdditionalChecks: () => void;
    clearMenusChecks: () => void;
    clearWebOpChecks: () => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    theme: string;
    menusReturnCode: string;
    locale: string,
    mainPaneViewOrder: IView[];
    countryCode: string;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
}

const AddSkipServices = (props: IProps) => {
    const [boxOpenFlag, setBoxOpenFlag] = useState(false);
    const [moveFlag, setMoveFlag] = useState('');
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;
    const [goToCalendarSelect, setGoToCalendarSelect] = useState(false);

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y' ||
        props.specData?.jaguarTrackingFlag === 'Y' ||
        props.specData?.landRoverTrackingFlag === 'Y';

    useEffect(() => {
        if (props.specDeptData === BlankDeptSpec) {
            props.getDeptSpecs();
        }
    }, [props.specDeptData, props.getDeptSpecs]);

    const skipOpenHandler = useCallback((e: any) => {
        // if spec flag is set to not display modal then we use skipProceed
        setBoxOpenFlag(true);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.SkipServiceBtnText,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    const skipCloseHandler = useCallback((e: any) => {
        setBoxOpenFlag(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Cancel,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    // if the moveFlag is set and we have gotten back invalidDates, then go to the next screen.
    useEffect(() => {
        if (moveFlag === 'T' && goToCalendarSelect) {
            setMoveFlag('');
        }
    }, [moveFlag, props.pushToMainPanes, goToCalendarSelect]);

    useEffect(() => {
        const currentScreen = props.mainPaneViewOrder[props.mainPaneViewOrder?.length -1] ?? '';
        if (currentScreen.viewId === 'AddSkipServices' || currentScreen.viewId === 'ServiceTab') {
            if (props.apptOps.length === 1 && props.apptOps[0].id === 'skipReason') {
                props.UpdateApptOps([]);
            }
        }
    }, [props.mainPaneViewOrder, props.apptOps, props.UpdateApptOps]);

    const skipProceed = useCallback((reason: string) => {
        let apptOpsInp: UpdateApptOpsInp[] = [];
        apptOpsInp = [
            {
                id: 'skipReason',
                operation: 'reason',
                concern: reason,
                estTime: '',
                menuID: '',
                spgOp: '',
                opType: OpType.SkipServiceReason,
                department: props.specDefaultDept,
                itemCostLow: '****',
                itemCostHigh: '****',
                opWaiter: '',
                opLoaner: '',
                opRental: '',
                opConcierge: '',
                opCadillac: '',
                opPPUDWL: '',
            },
        ];
        props.UpdateApptOps(apptOpsInp);
        props.clearAdditionalChecks();
        props.clearMenusChecks();
        props.clearWebOpChecks();
        const defaultDept = props.specDefaultDept;
        const determineDept = {
            defaultDept: defaultDept,
        };
        props.determineDept(determineDept);
        props.getDeptSpecs();
        // If recoommend Times is active in specs and we have a valid customer then we want to go to
        // the QuickSelect screen.  But we only want to go there if we have a lastAdv or lastTrans for
        // vehicle.  This is based on if specs indicates if recommended times is based on Adv or Trans
        let skipQuickTimes = true;
        if (props.specDeptData.recommTimes === 'Y' && props.apptCust?.custFirstName !== '' &&
            props.apptCust?.custNameId !== '') {
            skipQuickTimes = false;
        }
        if (skipQuickTimes) {
            props.pushToMainPanes({
                viewId: ComponentMapIds.CalendarSelect,
                component: ComponentMapIds.CalendarSelect,
            });
            setGoToCalendarSelect(true);
            setMoveFlag('T');
        } else {
            props.pushToMainPanes({
                viewId: ComponentMapIds.QuickTime,
                component: ComponentMapIds.QuickTime,
            });
        }
        setBoxOpenFlag(false);
        props.addLogEntry('Skip Services Selected', '', '');
        const ascRetailProcessInp: ascRetailProcessInput = {
            pageType: 'service',
            flowStage: '4b',
            flowOutcome: 'proceed',
            flowName: 'appointment',
            elementTitle: 'Skip Services',
        };
        ascRetailProcess(ascRetailProcessInp, props.vehicles, props.selectedVehicle);
    // }, [props?.UpdateApptOps, props?.addLogEntry, props?.apptCust?.custFirstName, props?.apptDept,
    //     props?.apptOps, props?.clearInvalidDates, props?.determineDept, props?.getInvalidDates, props?.invalidDatesDept,
    //     props?.pushToMainPanes, props?.selectedVehicle, props?.specDefaultDept,
    //     props?.specDeptData?.recTimesCtgry, props?.specDeptData?.recommTimes, props?.specProfile, props?.vehicles]);
    }, [props?.UpdateApptOps, props?.addLogEntry, props?.apptCust?.custFirstName,
        props.clearAdditionalChecks,
        props?.determineDept,
        props?.pushToMainPanes, props?.specDefaultDept,
        props?.specDeptData, props.getDeptSpecs,
        props.selectedVehicle, props.vehicles,
        props.clearMenusChecks, props.clearWebOpChecks, props.apptCust?.custNameId]);

    const onAddHandler = (e: any) => {
        // This button will take the user to the We Owes, Recalls and Recommended page
        // if they are an existing consumer and their vehicle has any of those applicable to their vehicle
        // const test = true;
        let nextViewId = ComponentMapIds.AdditionalServices;
        let nextComponent = ComponentMapIds.AdditionalServices;
        const maintMake = props.selectedVehicle && props.selectedVehicle !== 'AddVehicle' ?
            props.vehicles[props.selectedVehicle].make.toLowerCase().split(' ')[0]:'';
        // const maintContractView = (props.selectedVehicle && props.locale === 'en-US' &&
        const maintContractView = (props.selectedVehicle && props.countryCode === 'US' &&
            (((props.lexusMCLaunched || props.lexusTDMCLaunched) && maintMake==='lexus') ||
            ((props.toyotaMCLaunched || props.toyotaTDMCLaunched) && maintMake==='toyota')));
        if (!props.apptVin || (!props.showWeOwes && !props.showPrvRecom && (!props.showOEMRecall || !props.inquiryInvoked) &&
            !maintContractView)) {
            nextViewId = ComponentMapIds.ServiceTab;
            nextComponent = ComponentMapIds.ServiceTab;
        }
        if (!props.additionalServices.allServices.weOwes[0] && !props.additionalServices.allServices.recom[0] &&
            !props.additionalServices.allServices.inquiryInvoked && !maintContractView) {
            nextViewId = ComponentMapIds.ServiceTab;
            nextComponent = ComponentMapIds.ServiceTab;
        }

        props.pushToMainPanes({
            viewId: nextViewId,
            component: nextComponent,
        });
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.AddServiceBtnText,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.AddServiceBtnText,
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);

        if (isSDAnalyticsEnabled) {
            sendSdEvent('scheduleServiceSelectionShown', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                    props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                scheduleServiceVehicleMake: props.apptVeh.make,
                scheduleServiceVehicleMileage: getSdMileage(props.apptVeh.mileage),
                scheduleServiceVehicleModel: props.apptVeh.model,
                scheduleServiceVehicleVin: props.apptVeh.vin,
                scheduleServiceVehicleYear: props.apptVeh.vehYr,
            });
        }
    };

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Add/Skip Services', '');
    }, [
        props.addLogEntry,
    ]);

    useEffect(() => {
        if (props.mainPaneViewOrder){
            if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId === ComponentMapIds.AddSkipServices) {
                const ascPageViewInp: ascPageviewInput = {
                    pageType: 'service',
                    errorCode: '',
                    itemResults: '',
                };
                ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
            }
        }
    }, [props.mainPaneViewOrder, props.vehicles, props.selectedVehicle]);

    const skipBoxCSS = classNames(
        css.skipBoxText,
        {
            [css.skipBoxTextMINI]: props.theme === 'mini',
            [css.skipBoxTextSPM]: props.theme === 'toyotaSPM',
            [css.skipBoxTextAcura]: props.theme === 'acura-ca',
            [css.skipBoxTextChevrolet]: props.theme === 'chevrolet',
            [css.skipBoxTextHonda]: props.theme === 'honda',
            [css.skipBoxTextFord]: props.theme === 'ford',
            [css.skipBoxTextBuick]: props.theme === 'buick',
            [css.skipBoxTextBMW]: props.theme === 'bmw',
            [css.skipBoxTextInfiniti]: props.theme === 'infiniti',
            [css.skipBoxTextNissan]: props.theme === 'nissan',
            [css.skipBoxTextMB]: props.theme === 'mercedesBenz',
        },
    );

    const addBoxCSS = classNames(
        css.addBoxText,
        {
            [css.addBoxTextCadillac]: props.theme === 'cadillac',
            [css.addBoxTextSPM]: props.theme === 'toyotaSPM',
            [css.addBoxTextInfiniti]: props.theme === 'infiniti',
            [css.addBoxTextHonda]: props.theme === 'honda',
            [css.addBoxTextChevrolet]: props.theme === 'chevrolet',
            [css.addBoxTextFord]: props.theme === 'ford',
            [css.addBoxTextBuick]: props.theme === 'buick',
            [css.addBoxTextMB]: props.theme === 'mercedesBenz',
        },
    );

    let longLitFlag = '';
    if (strings.AddSvcsTip.length > 17) {
        longLitFlag = 'A';
    }

    let primaryButtonVariant = 'primaryBtnAlt2';
    let secondaryButtonVariant = 'secondaryBtnAlt2';
    switch (props.theme) {
        case 'lexusMonogram':
            primaryButtonVariant = 'primaryBtnAltLM';
            secondaryButtonVariant = 'secondaryBtnAltLM';
            break;
        case 'chevrolet':
            primaryButtonVariant = 'primaryBtnAlt2CV';
            secondaryButtonVariant = 'secondaryBtnAlt2CV';
            break;
        case 'bmw':
            primaryButtonVariant = 'primaryBtnAltBMW';
            secondaryButtonVariant = 'secondaryBtnAltBMW';
            break;
        case 'mercedesBenz':
            secondaryButtonVariant = 'secondaryBtnAltMB';
            break;
        case 'acura':
            secondaryButtonVariant = 'secondaryBtnAltAcura';
            break;
        default:
            break;
    }

    const addDivClass = classNames(
        css.addDiv,
        {
            [css.addDivMB]: props.theme === 'mercedesBenz',
        },
    );

    const skipDivClass = classNames(
        css.skipDiv,
        {
            [css.skipDivMB]: props.theme === 'mercedesBenz',
        },
    );

    return (
        <React.Fragment>
            <ScrollBar scrollVertical>
                <div className={css.AddSkipServices}>
                    <SkipLightBox
                        cancel={skipCloseHandler}
                        proceed={skipProceed}
                        specSkipMsg={props.specData?.skipSVCMsg}
                        dialogOpen={boxOpenFlag}
                        theme={props.theme}
                        selectedVehicle={props.selectedVehicle}
                        vehicles={props.vehicles}
                    />
                    {moveFlag || (props.menusReturnCode === '') ? <LoadingProgress size={100} /> : null}
                    <div className={css.mainFlex}>
                        <div className={css.mainDiv}>
                            <div className={addDivClass}>
                                <p className={addBoxCSS}>{strings.AddServices}</p>
                                {props.theme === 'toyotaSPM' ?
                                    <AddSkipButton
                                        id='Add'
                                        handler={onAddHandler}
                                        message={strings.AddServiceBtnText}
                                        isDisabled={boxOpenFlag}
                                        variant={'primaryASBtn'}
                                        tooltip={strings.AddSvcsTip}
                                        longLitFlag={longLitFlag}
                                        noArrow={noArrow} /> :
                                    <SVPButton
                                        id='Add'
                                        handler={onAddHandler}
                                        message={strings.AddServiceBtnText}
                                        isDisabled={boxOpenFlag}
                                        variant={primaryButtonVariant}
                                        tooltip={strings.AddSvcsTip}
                                        longLitFlag={longLitFlag}
                                        theme={props.theme}
                                        noArrow={noArrow} />
                                }
                            </div>
                            <div className={css.circleDiv}>
                                <TextInCircle text={strings.OrLiteral} theme={props.theme} />
                            </div>
                            <div className={skipDivClass}>
                                <p className={skipBoxCSS}>{strings.SkipServices}</p>
                                {props.theme === 'toyotaSPM' ?
                                    <AddSkipButton
                                        id='Skip'
                                        handler={skipOpenHandler}
                                        message={strings.SkipServiceBtnText}
                                        isDisabled={boxOpenFlag}
                                        variant={'secondaryASBtn'}
                                        tooltip={strings.SkipServcTip}
                                        longLitFlag={longLitFlag}
                                        noArrow={noArrow} /> :
                                    <AddSkipButton
                                        id='Skip'
                                        handler={skipOpenHandler}
                                        message={strings.SkipServiceBtnText}
                                        isDisabled={boxOpenFlag}
                                        variant={secondaryButtonVariant}
                                        tooltip={strings.SkipServcTip}
                                        longLitFlag={longLitFlag}
                                        theme={props.theme}
                                        noArrow={noArrow} />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollBar>
            <Footer
                id='AddSkipServicesFooter'
                handler={() => null}
                message={strings.Confirm}
                secondaryhandler={() => null}
                noButtons
            />
        </React.Fragment>

    );
};

export default AddSkipServices;